<template>
    <div>
			<h1 class="my-5"><strong>Créez</strong> votre entreprise</h1>

			<b-row class="mb-5">
				<b-col cols="auto">
					<img :src="assistant.src" :alt="assistant.alt" class="mr-3" />
				</b-col>

				<b-col>
					<div class="bg-light p-3 rounded">
						<p>Très bien !</p>
						<p>Maintenant, merci de renseigner <span v-if="services.creation.company.legalForm !== 'SAS / SASU'">les éventuels dirigeants / associés</span> <span v-else>le président et les actionnaires</span> de votre entreprise.</p>
					</div>
				</b-col>
			</b-row>

			<b-form class="mb-5">
				<b-form-group class="mb-5">
					<label class="h4 font-weight-bold">
						Personne à contacter pour les formalités

						<b-button id="tooltip" type="button" size="sm" pill>?</b-button>

						<b-tooltip target="tooltip" triggers="hover" variant="primary">
							<p>La personne sera la personne à contacter pour toutes informations manquantes nécessaire au dossier</p>
						</b-tooltip>
					</label>

					<b-form-row class="mb-2">
						<b-col cols="12" md="6">
							<b-form-group label="Prénom" label-for="representative-first-name" invalid-feedback="Le prénom du représentant est obligatoire.">
								<b-input
								id="representative-first-name"
								v-model.trim="$v.representative.name.firstName.$model"
								:state="validateState($v.representative.name.firstName)" />
							</b-form-group>
						</b-col>

						<b-col cols="12" md="6">
							<b-form-group label="Nom" label-for="representative-last-name" invalid-feedback="Le nom du représentant est obligatoire.">
								<b-input
								id="representative-last-name"
								v-model.trim="$v.representative.name.lastName.$model"
								:state="validateState($v.representative.name.lastName)" />
							</b-form-group>
						</b-col>
					</b-form-row>

					<b-form-row class="mb-2">
						<b-col cols="12" md="6">
							<b-form-group
							label="Numéro de téléphone"
							label-for="representative-phone">
								<b-input-group prepend="📞">
									<b-input
									id="representative-phone"
									v-model.trim="$v.representative.phone.$model"
									aria-describedby="representative-phone-live-feedback"
									:state="validateState($v.representative.phone)" />

									<b-form-invalid-feedback id="representative-phone-live-feedback">
										Le numéro de téléphone est obligatoire, doit être au format numérique et comporter 10 chiffres.
									</b-form-invalid-feedback>
								</b-input-group>
							</b-form-group>
						</b-col>

						<b-col cols="12" md="6">
							<b-form-group
							label="Adresse mail"
							label-for="representative-email">
								<b-input-group prepend="@">
									<b-input
									id="representative-email"
									v-model.trim="$v.representative.mailAddress.$model"
									aria-describedby="representative-mail-address-live-feedback"
									:state="validateState($v.representative.mailAddress)" />

									<b-form-invalid-feedback id="representative-mail-address-live-feedback">
										L'adresse mail est obligatoire et doit être au format xxx@xxx.xx
									</b-form-invalid-feedback>
								</b-input-group>
							</b-form-group>
						</b-col>
					</b-form-row>

					<b-button class="btn" @click="addRepresentativeAsLeader()">
						Ajouter en tant que <span v-if="services.creation.company.legalForm !== 'SAS / SASU'">dirigeant</span> <span v-else>président</span>
					</b-button>
				</b-form-group>

				<b-form-group class="mb-5" v-if="services.creation.company.legalForm !== 'SAS' && services.creation.company.legalForm !== 'SASU'">
					<label class="h4 font-weight-bold">Dirigeants</label>

					<b-tabs justified>
						<b-tab
						v-for="(leader, index) in leaders" :key="index"
						:title="tabTitle(index)"
						active
						class="pt-3">
							<LeaderTab
							:index="index"
							:leader="leader"
							:countriesList="countriesList"
							:citiesList="citiesList"
							:bossNaming="'dirigeant'"
							@get-cities-by-zip-code="getCitiesByZipCode"
							@add-leader-as-associate="addLeaderAsAssociate"
							@add-leader="addLeader"
							@delete-leader="deleteLeader"
							@update-leaders="updateLeaders"
							@add-birthday-slash="addBirthdaySlash"
							@add-foreign-zip-code="addForeignZipCode"
							@can-continue="canContinue" />
						</b-tab>
					</b-tabs>
				</b-form-group>

				<b-form-group v-else class="mb-4">
					<label class="h4 font-weight-bold">Président</label>

					<LeaderTab
					:index="0"
					:leader="leaders[0]"
					:countriesList="countriesList"
					:citiesList="citiesList"
					:bossNaming="'président'"
					@get-cities-by-zip-code="getCitiesByZipCode"
					@add-leader-as-associate="addLeaderAsAssociate"
					@add-leader="addLeader"
					@delete-leader="deleteLeader"
					@update-leaders="updateLeaders"
					@add-birthday-slash="addBirthdaySlash"
					@add-foreign-zip-code="addForeignZipCode"
					@can-continue="canContinue" />
				</b-form-group>

				<b-button
				v-if="services.creation.company.legalForm === 'SAS' || services.creation.company.legalForm === 'SASU' && !services.creation.company.generalManager.exists"
				@click="addGeneralManager"
				class="btn btn-lg mb-5" >
					Ajouter un directeur général
				</b-button>

				<b-form-group v-if="services.creation.company.generalManager.exists" class="mb-5">
					<label class="h4 font-weight-bold">Directeur général</label>

					<b-form-group :label="'Le directeur général est une personne'" class="mb-2">
						<b-form-radio-group
						v-model="generalManager.type"
						buttons
						button-variant="outline-primary">
							<b-form-radio value="Physique">Physique</b-form-radio>
							<b-form-radio value="Morale">Morale</b-form-radio>
						</b-form-radio-group>
					</b-form-group>

					<b-form-group class="mb-1">
						<b-form-row>
							<b-col cols="12" md="4">
								<b-form-group label="Genre">
									<b-form-radio-group
									v-model="generalManager.sex"
									buttons
									button-variant="outline-primary">
										<b-form-radio value="Féminin">Féminin</b-form-radio>
										<b-form-radio value="Masculin">Masculin</b-form-radio>
									</b-form-radio-group>
								</b-form-group>
							</b-col>

							<b-col cols="12" md="4">
								<b-form-group label="Prénom" label-for="general-manager-first-name" :invalid-feedback="'Le prénom du directeur général est obligatoire.'">
									<b-input
									id="general-manager-first-name"
									v-model.trim="$v.generalManager.name.firstName.$model"
									:state="validateState($v.generalManager.name.firstName)" />
								</b-form-group>
							</b-col>

							<b-col cols="12" md="4" v-if="generalManager.sex === 'Masculin'">
								<b-form-group label="Nom" label-for="general-manager-last-name" :invalid-feedback="'Le nom du directeur général est obligatoire.'">
									<b-input
									id="general-manager-last-name"
									v-model.trim="$v.generalManager.name.lastName.$model"
									:state="validateState($v.generalManager.name.lastName)" />
								</b-form-group>
							</b-col>

							<b-col cols="12" md="4" v-else>
								<b-form-group label="Nom de naissance" label-for="general-manager-birth-name" :invalid-feedback="'Le nom de naissance du directeur général est obligatoire.'">
									<b-input
									id="general-manager-birth-name"
									v-model.trim="$v.generalManager.name.birthName.$model"
									:state="validateState($v.generalManager.name.birthName)" />
								</b-form-group>
							</b-col>
						</b-form-row>
					</b-form-group>

					<div v-if="generalManager.type === 'Physique'">
						<b-form-row class="mb-1">
							<b-col cols="12" md="4">
								<b-form-group label="Statut marital" label-for="general-manager-marital-status" :invalid-feedback="'Le statut marital du directeur général est obligatoire'">
									<b-form-select
									id="general-manager-marital-status"
									v-model="generalManager.maritalStatus"
									:options="maritalGender"
									:state="validateState($v.generalManager.maritalStatus)" />
								</b-form-group>
							</b-col>

							<b-col cols="12" md="4" v-if="generalManager.maritalStatus === 'Mariée' && generalManager.sex === 'Féminin'">
								<b-form-group label="Nom marital" label-for="general-manager-marital-name" :invalid-feedback="'Le nom marital du directeur général est obligatoire.'">
									<b-input
									id="general-manager-marital-name"
									v-model.trim="$v.generalManager.name.maritalName.$model"
									:state="validateState($v.generalManager.name.maritalName)" />
								</b-form-group>
							</b-col>
						</b-form-row>

						<b-form-group class="mb-1">
							<b-form-row>
								<b-col cols="12" md="3" class="mb-3">
									<b-form-group
										label="Date de naissance"
										label-for="general-manager-birthday"
										:invalid-feedback="'La date de naissance du directeur général est obligatoire et doit être au format jj/mm/yyyy.'">
											<b-input
											id="general-manager-birthday"
											v-model.trim="$v.generalManager.birth.day.$model"
											:state="validateState($v.generalManager.birth.day)"
											@input="setDate($event)" />
									</b-form-group>
								</b-col>

								<b-col cols="12" md="3">
									<b-form-group
										label="Pays de naissance"
										label-for="general-manager-birth-country"
										:invalid-feedback="'Le pays de naissance du directeur général est obligatoire'">
											<b-form-select
											id="general-manager-birth-country"
											v-model="generalManager.birth.country"
											:options="countriesList"
											:state="validateState($v.generalManager.birth.country)"
											@change="setZipCode($event)" />
									</b-form-group>
								</b-col>

								<b-col cols="12" md="3" v-if="generalManager.birth.country === 'France'">
									<b-form-group
									label="Code postal de naissance"
									label-for="general-manager-birth-zipCode"
									:invalid-feedback="'Le code postal de naissance du directeur général est obligatoire, doit être au format numérique et comporter 5 chiffres.'">
										<b-input
										id="general-manager-birth-zipCode"
										v-model.trim="$v.generalManager.birth.zipCode.$model"
										:state="validateState($v.generalManager.birth.zipCode)"
										@change="getCitiesByZipCode" />
									</b-form-group>
								</b-col>

								<b-col cols="12" md="3">
									<b-form-group label="Ville de naissance" label-for="general-manager-birth-city" :invalid-feedback="'Le lieu de naissance du directeur général est obligatoire'">
										<b-input
										id="general-manager-birth-city"
										list="citiesList"
										v-model.trim="$v.generalManager.birth.city.$model"
										:state="validateState($v.generalManager.birth.city)" />

										<b-datalist id="citiesList" :options="citiesList" />
									</b-form-group>
								</b-col>
							</b-form-row>
						</b-form-group>

						<b-form-row class="mb-1">
							<b-col cols="12" md="6">
								<b-form-group
								label="Nationalité"
								label-for="general-manager-birth-nationality"
								:invalid-feedback="'La nationalité du directeur général est obligatoire'">
										<b-input-group prepend="🌐">
												<b-input
												id="general-manager-birth-nationality"
												v-model.trim="$v.generalManager.birth.nationality.$model"
												:state="validateState($v.generalManager.birth.nationality)" />
										</b-input-group>
								</b-form-group>
							</b-col>
						</b-form-row>

						<b-form-row class="mb-2">
							<b-col cols="12" md="6">
								<b-form-group label="Adresse" label-for="general-manager-address" :invalid-feedback="'L\'adresse du directeur général est obligatoire'">
									<b-input
									id="general-manager-address"
									v-model.trim="$v.generalManager.address.place.$model"
									:state="validateState($v.generalManager.address.place)" />
								</b-form-group>
							</b-col>

							<b-col cols="12" md="2">
								<b-form-group
								label="Code postal"
								label-for="general-manager-zip-code"
								:invalid-feedback="'Le code postal du directeur général est obligatoire, doit être au format numérique et comporter 5 chiffres.'">
									<b-input
									id="general-manager-zip-code"
									v-model.trim="$v.generalManager.address.zipCode.$model"
									:state="validateState($v.generalManager.address.zipCode)"
									@change="getCitiesByZipCode" />
								</b-form-group>
							</b-col>

							<b-col cols="12" md="4">
								<b-form-group label="Ville" label-for="general-manager-city" :invalid-feedback="'La ville du directeur général est obligatoire'">
									<b-input
									id="general-manager-city"
									list="citiesList"
									v-model.trim="$v.generalManager.address.city.$model"
									:state="validateState($v.generalManager.address.city)" />

									<b-datalist id="citiesList" :options="citiesList" />
								</b-form-group>
							</b-col>
						</b-form-row>
					</div>

					<div v-else>
						<b-form-row class="mb-1">
							<b-col cols="12" md="4">
								<b-form-group label="Nom de la société" label-for="company-name" invalid-feedback="Le nom de la société est obligatoire">
									<b-input
									id="company-name"
									v-model.trim="$v.generalManager.company.name.$model"
									:state="validateState($v.generalManager.company.name)" />
								</b-form-group>
							</b-col>

							<b-col cols="12" md="4">
								<b-form-group label="Numéro RCS" label-for="company-rcs" invalid-feedback="Le numéro RCS est obligatoire.">
									<b-input
									type="number"
									id="company-rcs"
									v-model.trim="$v.generalManager.company.rcs.$model"
									:state="validateState($v.generalManager.company.rcs)" />
								</b-form-group>
							</b-col>
						</b-form-row>

						<b-form-group label="Adresse du siège social" label-for="head-office-address" invalid-feedback="L'adresse du siège social est obligatoire.">
							<b-input
							id="head-office-address"
							v-model.trim="$v.generalManager.company.headOffice.place.$model"
							:state="validateState($v.generalManager.company.headOffice.place)" />
						</b-form-group>

						<b-form-row class="mb-1">
							<b-col cols="12" md="4">
								<b-form-group
								label="Code postal"
								label-for="head-office-zip-code"
								invalid-feedback="Le code postal du siège social est obligatoire, doit être au format numérique et comporter 5 chiffres.">
									<b-input
									id="head-office-zip-code"
									v-model.trim="$v.generalManager.company.headOffice.zipCode.$model"
									:state="validateState($v.generalManager.company.headOffice.zipCode)"
									@change="getCitiesByZipCode" />
								</b-form-group>
							</b-col>

							<b-col cols="12" md="4">
								<b-form-group label="Ville" label-for="head-office-city" invalid-feedback="La ville du siège social est obligatoire">
									<b-input
									id="head-office-city"
									list="citiesList"
									v-model.trim="$v.generalManager.company.headOffice.city.$model"
									:state="validateState($v.generalManager.company.headOffice.city)" />

									<b-datalist id="citiesList" :options="citiesList" />
								</b-form-group>
							</b-col>

							<b-col cols="12" md="4">
								<b-form-group label="Ville du Greffe" label-for="head-office-registry" invalid-feedback="La ville du Greffe du siège social est obligatoire">
									<b-input
									id="head-office-registry"
									list="citiesList"
									v-model.trim="$v.generalManager.company.headOffice.registry.$model"
									:state="validateState($v.generalManager.company.headOffice.registry)" />

									<b-datalist id="citiesList" :options="citiesList" />
								</b-form-group>
							</b-col>
						</b-form-row>

						<b-form-row class="mb-1">
							<b-col>
								<b-form-group
								label="Capital social"
								label-for="general-manager-company-share-capital">
									<b-input-group>
										<b-input
										type="number"
										id="general-manager-company-share-capital"
										v-model.trim="$v.generalManager.company.shareCapital.$model"
										aria-describedby="general-manager-company-share-capital-live-feedback"
										:state="validateState($v.generalManager.company.shareCapital)" />

										<div class="input-group-append">
											<span class="input-group-text">€</span>
										</div>

										<b-form-invalid-feedback id="general-manager-company-share-capital-live-feedback">
											Le capital social est oligatoire
										</b-form-invalid-feedback>
									</b-input-group>
								</b-form-group>
							</b-col>

							<b-col>
								<b-form-group
								label="Forme sociale"
								label-for="general-manager-company-social-form"
								invalid-feedback="La forme sociale est obligatoire.">
									<b-input
									id="general-manager-company-social-form"
									v-model.trim="$v.generalManager.company.socialForm.$model"
									placeholder="SARL, SASU, etc..."
									:state="validateState($v.generalManager.company.socialForm)" />
								</b-form-group>
							</b-col>
						</b-form-row>
					</div>

					<b-form-row class="mt-3">
						<b-col cols="auto">
							<b-button class="btn" @click="addGeneralManagerAsAssociate">
								Ajouter en tant qu'actionnaire
							</b-button>
						</b-col>

						<b-col>
							<b-button class="btn btn-danger" @click="removeGeneralManager">
								Supprimer
							</b-button>
						</b-col>
					</b-form-row>
				</b-form-group>

				<b-form-group v-if="services.creation.company.legalForm !== 'SASU'">
						<label v-if="services.creation.company.legalForm === 'SAS'" class="h4 font-weight-bold">Actionnaires</label>
						<label v-else class="h4 font-weight-bold">Associés</label>

						<b-tabs justified>
							<b-tab
							v-for="(associate, index) in associates" :key="index"
							:title="tabTitle(index)"
							active
							class="pt-3">
								<AssociateTab
									:index="index"
									:associate="associate"
									:countriesList="countriesList"
									:citiesList="citiesList"
									:associateNaming="associateNaming"
									@get-cities-by-zip-code="getCitiesByZipCode"
									@add-associate="addAssociate"
									@delete-associate="deleteAssociate"
									@update-associates="updateAssociates"
									@add-birthday-slash="addBirthdaySlash"
									@add-foreign-zip-code="addForeignZipCode"
									@can-continue="canContinue" />
							</b-tab>
						</b-tabs>
				</b-form-group>

				<b-form-group v-else>
					<label class="h4 font-weight-bold">Actionnaire</label>

					<AssociateTab
					:index="0"
					:associate="associates[0]"
					:countriesList="countriesList"
					:citiesList="citiesList"
					:associateNaming="associateNaming"
					@get-cities-by-zip-code="getCitiesByZipCode"
					@add-associate="addAssociate"
					@delete-associate="deleteAssociate"
					@update-associates="updateAssociates"
					@add-birthday-slash="addBirthdaySlash"
					@add-foreign-zip-code="addForeignZipCode"
					@can-continue="canContinue" />
				</b-form-group>
			</b-form>
    </div>
</template>

<script>
    import { mapState } from 'vuex';

    import { required, numeric, email, minLength, maxLength } from "vuelidate/lib/validators";

    import axios from 'axios';

    import AssociateTab from './AssociateTab.vue';
    import LeaderTab from './LeaderTab.vue';

    export default {
			components: {
				AssociateTab,
				LeaderTab
			},
			props: ['clickedNext'],
			data() {
				return {
						representative: {
							name: {
								firstName: null,
								lastName: null
							},
							phone: null,
							mailAddress: null
						},
						generalManager: {
							exists: false,
							type: 'Physique',
							company: {
								name: null,
								rcs: null,
								headOffice: {
									place: null,
									zipCode: null,
									city: null,
									registry: null
								},
								shareCapital: null,
								socialForm: null
							},
							sex: "Féminin",
							name: {
								lastName: null,
								birthName: null,
								firstName: null,
								maritalName: null
							},
							maritalStatus: "Célibataire",
							birth: {
								day: null,
								zipCode: null,
								city: null,
								country: null,
								nationality: null
							},
							address: {
								place: null,
								zipCode: null,
								city: null
							}
						},
						defaultLeader: {
							type: 'Morale',
							company: {
								name: null,
								rcs: null,
								headOffice: {
									place: null,
									zipCode: null,
									city: null,
									registry: null
								},
								shareCapital: null,
								socialForm: null
							},
							sex: 'Féminin',
							name: {
								lastName: null,
								birthName: null,
								firstName: null,
								maritalName: null
							},
							maritalStatus: 'Célibataire',
							birth: {
								day: null,
								zipCode: null,
								city: null,
								country: null,
								nationality: null
							},
							address: {
								place: null,
								zipCode: null,
								city: null
							},
							motherName: null,
							fatherName: null
						},
						leaders: [
							{
								type: 'Physique',
								company: {
									name: null,
									rcs: null,
									headOffice: {
										place: null,
										zipCode: null,
										city: null,
										registry: null
									},
									shareCapital: null,
									socialForm: null
								},
								sex: "Féminin",
								name: {
									lastName: null,
									birthName: null,
									firstName: null,
									maritalName: null
								},
								maritalStatus: "Célibataire",
								birth: {
									day: null,
									zipCode: null,
									city: null,
									country: null,
									nationality: null
								},
								address: {
									place: null,
									zipCode: null,
									city: null
								},
								motherName: null,
								fatherName: null
							}
						],
						defaultAssociate: {
							type: 'Morale',
							company: {
								name: null,
								rcs: null,
								headOffice: {
									place: null,
									zipCode: null,
									city: null,
									registry: null
								},
								shareCapital: null,
								socialForm: null
							},
							sex: "Féminin",
							name: {
								lastName: null,
								birthName: null,
								firstName: null,
								maritalName: null
							},
							maritalStatus: 'Célibataire',
							birth: {
								day: null,
								zipCode: null,
								city: null,
								country: null,
								nationality: null
							},
							address: {
								place: null,
								zipCode: null,
								city: null
							},
							marriedUnderCommunityOfProperty: true,
							contribution: {
								numerary: {
									active: false,
									amount: null
								},
								inKind: {
									active: false,
									contributions: [null]
								}
							},
							alsoLeader: false
						},
						associates: [
							{
								type: 'Physique',
								company: {
									name: null,
									rcs: null,
									headOffice: {
										place: null,
										zipCode: null,
										city: null,
										registry: null
									},
									shareCapital: null,
									socialForm: null
								},
								sex: "Féminin",
								name: {
									lastName: null,
									birthName: null,
									firstName: null,
									maritalName: null
								},
								maritalStatus: "Célibataire",
								birth: {
									day: null,
									zipCode: null,
									city: null,
									country: null,
									nationality: null
								},
								address: {
									place: null,
									zipCode: null,
									city: null
								},
								marriedUnderCommunityOfProperty : true,
								contribution: {
									numerary: {
										active: false,
										amount: null
									},
									inKind: {
										active: false,
										contributions: [null]
									}
								},
								alsoLeader: false
							}
						],
						citiesList: []
				};
			},
			watch: {
				$v: {
					handler: function (val) {
						if (this.representative.name.firstName) {
							this.representative.name.firstName = this.representative.name.firstName.charAt(0).toUpperCase() + this.representative.name.firstName.substring(1).toLowerCase();
						}

						if (this.representative.name.lastName) {
							this.representative.name.lastName = this.representative.name.lastName.toUpperCase();
						}

						this.$store.commit('updateRepresentative', { representative: this.representative });
						this.$store.commit('updateGeneralManager', { generalManager: this.generalManager });

						if (!val.$invalid) {
							this.$emit('can-continue', { value: true });
						} else {
							this.$emit('can-continue', { value: false });
						}
					},
					deep: true
				},
				clickedNext () {
					this.$v.$touch();
				}
			},
			validations () {
				let datas = {
					representative: {
						name: {
							firstName: { required },
							lastName: { required }
						},
						phone: {
							required,
							numeric,
							minLength : minLength(10),
							maxLength : maxLength(10)
						},
						mailAddress: {
							required,
							email
						}
					}
				}

				if (this.services.creation.company.generalManager.exists) {
					Object.assign(datas, {
						generalManager: {
							name: {
								firstName: { required }
							}
						}
					})
					if (this.generalManager.sex === "Masculin") {
						datas.generalManager.name.lastName = { required }
					} else {
						Object.assign(datas.generalManager.name, {
							birthName : { required },
							maritalName : { required }
						})
					}

					if (this.generalManager.type === "Physique") {
						Object.assign(datas.generalManager, {
							maritalStatus: { required },
							birth: {
								day: {
									required,
									minLength : minLength(10),
									maxLength : maxLength(10)
								},
								zipCode: {
									required,
									numeric,
									minLength : minLength(5),
									maxLength : maxLength(5)
								},
								city: { required },
								country: {required},
								nationality: { required }
							},
							address: {
								place: { required },
								zipCode: {
									required,
									numeric,
									minLength : minLength(5),
									maxLength : maxLength(5)
								},
								city: { required }
							},
							motherName: { required },
							fatherName: { required }
						});
					} else {
						datas.generalManager.company = {
							name: { required },
							rcs: { required, numeric },
							headOffice: {
								place: { required },
								zipCode: {
									required,
									numeric,
									minLength : minLength(5),
									maxLength : maxLength(5)
								},
								city: { required },
								registry: { required }
							},
							shareCapital: { required },
							socialForm: { required }
						}
					}
				}

				return datas
			},
			methods: {
				validateState (v) {
					if (!v.$dirty) {
						return null;
					} else {
						return (v.$error) ? false : true
					}
				},
				canContinue (value) {
					if (value === true)
						this.$emit('can-continue', { value: true });
					else
						this.$emit('can-continue', { value: false });
				},
				addRepresentativeAsLeader () {
					Object.assign(this.leaders[0], JSON.parse(JSON.stringify(this.representative)));
					this.leaders[0].name.birthName = this.representative.name.lastName;
				},
				addLeaderAsAssociate (index) {
					let associatesLength = this.associates.length;

					if (this.associates.length === 1 && this.associates[0].name.firstName === null) {
						Object.assign(this.associates[0], JSON.parse(JSON.stringify(this.leaders[index])));
						this.associates[0].alsoLeader = true;
					} else {
						this.addAssociate();
						associatesLength++
						Object.assign(this.associates[associatesLength - 1], JSON.parse(JSON.stringify(this.leaders[index])));
						this.associates[associatesLength - 1].alsoLeader = true;
					}
				},
				addLeader () {
					this.leaders.push(JSON.parse(JSON.stringify(this.defaultLeader)));

					this.updateLeaders();
				},
				deleteLeader (index) {
					this.leaders.splice(index, 1);
				},
				updateLeaders () {
					this.leaders.forEach(leader => {
						if (leader.name.firstName)
							leader.name.firstName = leader.name.firstName.charAt(0).toUpperCase() + leader.name.firstName.substring(1).toLowerCase();

						if (leader.sex === 'Féminin' && leader.name.birthName) {
							leader.name.birthName = leader.name.birthName.toUpperCase();
						} else if (leader.sex === 'Masculin' && leader.name.lastName) {
							leader.name.lastName = leader.name.lastName.toUpperCase();
						}
					});

					this.$store.commit('updateLeaders', { leaders: this.leaders });
				},
				addGeneralManager () {
					this.generalManager.exists = true
				},
				addGeneralManagerAsAssociate () {
					let associatesLength = this.associates.length;

					if (this.associates.length === 1 && this.associates[0].name.firstName === null) {
						Object.assign(this.associates[0], JSON.parse(JSON.stringify(this.generalManager)));
						this.associates[0].alsoLeader = true;
					} else {
						this.addAssociate();
						associatesLength++
						Object.assign(this.associates[associatesLength - 1], JSON.parse(JSON.stringify(this.generalManager)));
						this.associates[associatesLength - 1].alsoLeader = true;
					}
				},
				removeGeneralManager () {
					this.generalManager.exists = false
				},

				addAssociate () {
					this.associates.push(JSON.parse(JSON.stringify(this.defaultAssociate)));

					this.updateAssociates();
				},
				deleteAssociate (index) {
						this.associates.splice(index, 1);
				},
				updateAssociates () {
					this.associates.forEach(associate => {
						if (associate.name.firstName)
							associate.name.firstName = associate.name.firstName.charAt(0).toUpperCase() + associate.name.firstName.substring(1).toLowerCase()

						if (associate.sex === 'Féminin' && associate.name.birthName) {
							associate.name.birthName = associate.name.birthName.toUpperCase();
						} else if (associate.sex === 'Masculin' && associate.name.lastName) {
							associate.name.lastName = associate.name.lastName.toUpperCase();
						}
					});

					this.$store.commit('updateAssociates', { associates: this.associates });
				},
				setDate (event) {
					if (event.length === 2 || event.length === 5) {
						this.addBirthdaySlash("generalManager");
					}
				},
				setZipCode (event) {
					if (event !== 'France') {
						this.addForeignZipCode("generalManager");
					}
				},
				addBirthdaySlash (status, index) {
					if (status === "leader") {
						this.leaders[index].birth.day += '/';
					} else if (status === "associate") {
						this.associates[index].birth.day += '/';
					} else {
						this.generalManager.birth.day += '/';
					}
				},
				addForeignZipCode (status, index) {
					if (status === "leader") {
						this.leaders[index].birth.zipCode = '00090';
					} else if (status === "associate") {
						this.associates[index].birth.zipCode = '00090';
					} else {
						this.generalManager.birth.zipCode = '00090';
					}
				},
				getCitiesByZipCode (zipCode) {
					if (zipCode.length === 5) {
						let requestTarget = this.apiDomain + '/get-cities-by-zip-code';

						axios.post(requestTarget, {
							zipCode : zipCode
						})
						.then((response) => {
							let citiesByZipCode = response.data.citiesByZipCode;
							let cities = [];

							citiesByZipCode.forEach((city) => {
								cities.push(city.nomCommune)
							});

							this.citiesList = cities;
						});
					}
				},
				tabTitle (index) {
					if (index === 5) {
						return "+5";
					} else {
						index ++;
						const title = index.toString();
						return title;
					}
				}
			},
			computed: {
				...mapState(['appName', 'assistant', 'apiDomain', 'services', 'maritalStatus', 'countriesList']),
				maritalGender () {
					return (this.generalManager.sex === 'Masculin') ? this.maritalStatus.male : this.maritalStatus.female
				},
				legalFormStacked () {
					return (window.outerWidth > 823) ? false : true
				},
				associateNaming () {
					return (this.services.creation.company.legalForm !== 'SAS' && this.services.creation.company.legalForm !== 'SASU') ? 'associé' : 'actionnaire'
				}
			}
    }
</script>
